import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { RecipeStep, ScaledIngredient, StepMention } from '../api/types';
import useDismiss from './useDismiss';

type Props = {
  step: RecipeStep;
  ingredients: ScaledIngredient[];
};

/** A step's body, one paragraph per line, with each ingredient it names
 * tappable to show that ingredient's amount from the list. */
export default function StepText({ step, ingredients }: Props) {
  const [open, setOpen] = useState<number | null>(null);
  const openRef = useRef<HTMLSpanElement | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useDismiss(open !== null, openRef, close);

  const byId = new Map(ingredients.map((item) => [item.ingredient_id, item]));
  const mentions = (step.mentions ?? []).filter((m) => byId.has(m.ingredient_id));

  let offset = 0;
  const lines = step.body.split('\n').map((text) => {
    const line = { text, start: offset };
    offset += text.length + 1;
    return line;
  });

  return (
    <div className="viewer__body">
      {lines
        .filter((line) => line.text.trim())
        .map((line) => {
          const end = line.start + line.text.length;
          const inLine = mentions.filter((m) => m.start >= line.start && m.end <= end);
          return (
            <p key={line.start}>
              {segments(line.text, line.start, inLine).map((part) =>
                part.mention ? (
                  <Mention
                    key={part.at}
                    text={part.text}
                    item={byId.get(part.mention.ingredient_id)!}
                    open={open === part.at}
                    anchorRef={open === part.at ? openRef : undefined}
                    onToggle={() => setOpen(open === part.at ? null : part.at)}
                  />
                ) : (
                  <span key={part.at}>{part.text}</span>
                ),
              )}
            </p>
          );
        })}
    </div>
  );
}

type Segment = { at: number; text: string; mention: StepMention | null };

function segments(text: string, start: number, mentions: StepMention[]): Segment[] {
  const out: Segment[] = [];
  let cursor = start;
  for (const mention of [...mentions].sort((a, b) => a.start - b.start)) {
    if (mention.start < cursor) continue;
    if (mention.start > cursor)
      out.push({
        at: cursor,
        text: text.slice(cursor - start, mention.start - start),
        mention: null,
      });
    out.push({
      at: mention.start,
      text: text.slice(mention.start - start, mention.end - start),
      mention,
    });
    cursor = mention.end;
  }
  if (cursor < start + text.length)
    out.push({ at: cursor, text: text.slice(cursor - start), mention: null });
  return out;
}

function Mention({
  text,
  item,
  open,
  anchorRef,
  onToggle,
}: {
  text: string;
  item: ScaledIngredient;
  open: boolean;
  anchorRef?: React.RefObject<HTMLSpanElement | null>;
  onToggle: () => void;
}) {
  const amount = [item.quantity_display, item.unit].filter(Boolean).join(' ');
  const popover = useRef<HTMLSpanElement | null>(null);

  // Anchored at the word, so one near the right edge would run off the screen.
  useLayoutEffect(() => {
    const el = popover.current;
    if (!el) return;
    const overflow = el.getBoundingClientRect().right - (window.innerWidth - 16);
    if (overflow > 0) el.style.left = `${-overflow}px`;
  }, [open]);

  return (
    <span className="mention" ref={anchorRef}>
      <button
        type="button"
        className="mention__button"
        aria-expanded={open}
        aria-label={`${text}: show amount`}
        onClick={onToggle}
      >
        {text}
        <span className="mention__i" aria-hidden="true">
          i
        </span>
      </button>
      {open && (
        <span className="mention__popover" role="status" ref={popover}>
          {amount ? (
            <>
              <strong>{amount}</strong> {item.name}
            </>
          ) : (
            item.raw_text
          )}
        </span>
      )}
    </span>
  );
}
